import { useEffect, useRef, useCallback } from 'react';

const COLORS = [
  [212, 175, 55],   // antique gold
  [255, 215, 0],    // bright gold
  [255, 236, 139],  // light gold
  [201, 168, 76],   // warm gold
  [255, 245, 180],  // cream gold
];

class GoldParticle {
  constructor(x, y) {
    const angle = Math.random() * Math.PI * 2;
    const speed = Math.random() * 3.5 + 0.8;
    this.x = x;
    this.y = y;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed - Math.random() * 1.5;
    this.radius = Math.random() * 2.8 + 0.4;
    this.alpha = 0.9 + Math.random() * 0.1;
    this.decay = Math.random() * 0.018 + 0.012;
    this.gravity = 0.06;
    this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
    this.spin = (Math.random() - 0.5) * 0.3;
    this.angle = Math.random() * Math.PI * 2;
    // Some particles are sparkle-shaped, others are dots
    this.isStar = Math.random() > 0.5;
  }

  update() {
    this.vx *= 0.96;
    this.vy = this.vy * 0.96 + this.gravity;
    this.x += this.vx;
    this.y += this.vy;
    this.alpha -= this.decay;
    this.angle += this.spin;
  }

  draw(ctx) {
    if (this.alpha <= 0) return;
    const [r, g, b] = this.color;
    ctx.save();
    ctx.globalAlpha = this.alpha;
    ctx.translate(this.x, this.y);
    ctx.rotate(this.angle);
    if (this.isStar && this.radius > 1.2) {
      // Sparkle / 4-pointed star
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      const s = this.radius;
      ctx.beginPath();
      ctx.moveTo(0, -s * 2.2);
      ctx.lineTo(s * 0.5, -s * 0.5);
      ctx.lineTo(s * 2.2, 0);
      ctx.lineTo(s * 0.5, s * 0.5);
      ctx.lineTo(0, s * 2.2);
      ctx.lineTo(-s * 0.5, s * 0.5);
      ctx.lineTo(-s * 2.2, 0);
      ctx.lineTo(-s * 0.5, -s * 0.5);
      ctx.closePath();
      ctx.fill();
      // Bright center highlight
      ctx.fillStyle = `rgba(255,255,220,${this.alpha * 0.7})`;
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.5, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Glowing dot
      const grd = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius * 2);
      grd.addColorStop(0, `rgba(255,245,180,${this.alpha})`);
      grd.addColorStop(0.4, `rgba(${r},${g},${b},${this.alpha * 0.8})`);
      grd.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.beginPath();
      ctx.arc(0, 0, this.radius * 2, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();
    }
    ctx.restore();
  }
}

export const ClickSpark = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const rafRef = useRef(null);
  const activeRef = useRef(false);

  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particles.current = particles.current.filter(p => p.alpha > 0);
    particles.current.forEach(p => { p.update(); p.draw(ctx); });

    if (particles.current.length > 0) {
      rafRef.current = requestAnimationFrame(loop);
    } else {
      activeRef.current = false;
    }
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const onResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);

    const handleClick = (e) => {
      const count = 28;
      for (let i = 0; i < count; i++) {
        particles.current.push(new GoldParticle(e.clientX, e.clientY));
      }
      if (!activeRef.current) {
        activeRef.current = true;
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    window.addEventListener('click', handleClick, { passive: true });

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [loop]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9997,
      }}
      aria-hidden="true"
      data-testid="global-click-spark-layer"
    />
  );
};
